import { ReactText } from "react";
import * as React from "react";
import { AbstractReactFactory } from "@projectstorm/react-canvas-core";
import { NodeModel } from "@projectstorm/react-diagrams";
import { action, observable } from "mobx";
import {
  BaseModelOptions,
  GenerateModelEvent
} from "@projectstorm/react-canvas-core";
import { DiagramEngine } from "@projectstorm/react-diagrams-core";
import { OutputType } from "../treeBlockOutputsDataSource";
import { AdvancedPortModel } from "./AdvancedLinkModel";
import map from "lodash/map";
import { LocalOutput, treeBlockConfigurations } from "../schema";
import { TreeBlockWidget } from "./TreeBlockWidget/TreeBlockWidget";

import { LoggerFactory } from "lego-gui-lib/lib";
const log = LoggerFactory.create(`BodyWidget`);

export class DefaultTreeBlockFactory extends AbstractReactFactory<
  DefaultTreeBlockModel,
  DiagramEngine
> {
  constructor() {
    super("default-tree-block");
  }

  generateModel(event: GenerateModelEvent): DefaultTreeBlockModel {
    return new DefaultTreeBlockModel(event.initialConfig);
  }

  generateReactWidget(event: any): JSX.Element {
    return (
      <TreeBlockWidget
        engine={this.engine as DiagramEngine}
        node={event.model}
      />
    );
  }
}

export interface DefaultTreeBlockModelOptions extends BaseModelOptions {
  id?: string;
  name?: string;
  color?: string;
  config?: string;
  meta?: Record<string, any>;
}

export class DefaultTreeBlockModel extends NodeModel {
  @observable public name: string;
  @observable public color: string;
  @observable public config: string;
  @observable public meta: Record<string, any>;
  @observable public portsOut: AdvancedPortModel[];
  @observable public portsIn: AdvancedPortModel[];

  private log = LoggerFactory.create("DefaultTreeBlockModel");

  public constructor(options: DefaultTreeBlockModelOptions = {}) {
    super({
      ...options,
      type: "default-tree-block"
    });
    this.name = options.name || "";
    const color = options.color || "";
    this.config = options.config || "";
    this.meta = options.meta || { info: [], warning: [] };
    this.color =
      //@ts-ignore
      treeBlockConfigurations[options.name].color || color;
    this.portsOut = [];
    this.portsIn = [];
  }

  serialize() {
    return {
      ...super.serialize(),
      color: this.color,
      name: this.name,
      config: this.config,
      meta: this.meta
    };
  }

  deserialize(event: any): void {
    super.deserialize(event);
    this.color = event.data.color;
    this.name = event.data.name;
    this.meta = event.data.meta;
    this.portsOut = map(event.data.portsOutOrder, id => {
      return this.getPortFromID(id) as AdvancedPortModel;
    });
    this.portsIn = map(event.data.portsInOrder, id => {
      return this.getPortFromID(id) as AdvancedPortModel;
    });
  }

  @action
  updateMeta(meta: { info: string[]; warning: string[] }) {
    this.meta = meta;
  }

  @action
  updateConfig(config: string) {
    this.config = config;
  }

  @action
  removePort(port: AdvancedPortModel) {
    if (port.getOptions().in) {
      this.portsIn.splice(this.portsIn.indexOf(port), 1);
    } else {
      this.portsOut.splice(this.portsOut.indexOf(port), 1);
    }
    // remove from model override
    if (this.ports[port.getName()]) {
      delete this.ports[port.getName()];
    }
  }

  @action
  addPort(port: AdvancedPortModel) {
    super.addPort(port);
    if (port.getOptions().in) {
      if (this.portsIn.indexOf(port) === -1) {
        this.portsIn.push(port);
      }
    } else {
      if (this.portsOut.indexOf(port) === -1) {
        this.portsOut.push(port);
      }
    }
    return port;
  }

  @action
  public addOutPort(
    name: string,
    label: string | (() => string),
    type: OutputType,
    optionId: ReactText | undefined,
    isAddedByDefault = false
  ): AdvancedPortModel {
    const preparedLabel = typeof label == "function" ? label() : label;
    const port = new AdvancedPortModel(
      false,
      name,
      preparedLabel,
      type,
      optionId,
      isAddedByDefault
    );
    return this.addPort(port);
  }

  @action
  public addOutput(output: LocalOutput): AdvancedPortModel {
    return this.addOutPort(
      output.value,
      output.label,
      output.type,
      output.optionId,
      output.isAddedByDefault
    );
  }

  @action
  addInPort(portName: string, after = true) {
    const port = new AdvancedPortModel(
      true,
      portName,
      portName,
      OutputType.SIMPLE,
      portName
    );
    if (!after) {
      this.portsIn.splice(0, 0, port);
    }
    return this.addPort(port);
  }

  getOutPorts() {
    return this.portsOut;
  }

  public getOutPort(name: string): AdvancedPortModel | undefined {
    return this.portsOut.find(port => port.getName() === name);
  }

  public getOutPortById(
    id: string | number | undefined
  ): AdvancedPortModel | undefined {
    // @ts-ignore
    return this.portsOut.find(port => port.options.id == id);
  }

  public getOutPortByOptionId(optionId: string | number | undefined) {
    this.log.debug("port in get port", this.portsOut);
    return this.portsOut.find(port => port.getOptionId() === optionId);
  }

  getInPorts() {
    return this.portsIn;
  }
}
